import React, { useEffect, useCallback, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/Modal';

interface DeleteWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  workspaceName: string;
  isLoading?: boolean;
}

export default function DeleteWorkspaceModal({
  isOpen,
  onClose,
  onConfirm,
  workspaceName,
  isLoading = false
}: DeleteWorkspaceModalProps) {
  const [confirmationText, setConfirmationText] = useState('');
  const isConfirmEnabled = confirmationText === workspaceName && !isLoading;

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setConfirmationText('');
      onClose();
    }
  }, [isLoading, onClose]);

  // Reset confirmation text when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
    }
  }, [isOpen]);


  const handleConfirm = () => {
    if (isConfirmEnabled) {
      onConfirm();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConfirm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      canClose={!isLoading}
      size="md"
    >
      <ModalHeader
        title="Delete workspace"
        icon={<AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />}
      />

      <form onSubmit={handleSubmit}>
        <ModalBody>
        {/* Warning Message */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            This action will permanently delete the workspace <strong>"{workspaceName}"</strong>, including:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-300 space-y-1 mb-4">
            <li>All member access and invitations</li>
            <li>All analyses and recommendations</li>
            <li>All terraform implementations</li>
            <li>Any active subscriptions will be cancelled</li>
          </ul>
          <p className="text-red-600 dark:text-red-400 text-sm font-medium">
            This action cannot be undone.
          </p>
        </div>

        {/* Confirmation Input */}
        <div>
          <label
            htmlFor="workspace-name-confirmation"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Type the workspace name <strong>"{workspaceName}"</strong> to confirm:
          </label>
          <input
            id="workspace-name-confirmation"
            type="text"
            value={confirmationText}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder={workspaceName}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            autoComplete="off"
          />
        </div>
        </ModalBody>

        <ModalFooter>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isConfirmEnabled}
          className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Deleting...' : 'Delete workspace'}
        </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}